import { CountryCode } from '@carsys/enums/country-code'
import { AddToOrderMenu, ExternalCatalogType, ExternalPortal, OrderInPreparation } from '@cbgms/api'
import { useMemo } from 'react'
import { DashboardCatalogInfo } from '../components/catalog/types'
import { FormValues } from '../components/Form'

const LKQ_ORDER_SYSTEM = 'VPOM_AXBelgium'

interface Result {
  filteredExternalPortals: ExternalPortal[]
  filteredOrderInPreparation: OrderInPreparation[]
  filteredAddToOrderMenu: AddToOrderMenu[]
}

const KEYS = {
  TOOL_SPECIAL: ExternalCatalogType.TOOL_SPECIAL,
  UNIVERSAL_PARTS: ExternalCatalogType.UNIVERSAL_PARTS,
  EQUIPMENT_SPECIAL: ExternalCatalogType.EQUIPMENT_SPECIAL,
  TYRE_BANDEN_NU: ExternalCatalogType.TYRE_BANDEN_NU,
  HAYNES_PRO: ExternalCatalogType.HAYNES_PRO
} as const

function baseDashboardCatalogByCountry(country: CountryCode): Set<string> {
  switch (country) {
    case CountryCode.NL:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS])
    case CountryCode.BE:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS, KEYS.EQUIPMENT_SPECIAL])
    case CountryCode.FR:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS])
    default:
      return new Set()
  }
}

function baseOrderInPreparationByCountry(country: CountryCode): Set<string> {
  switch (country) {
    case CountryCode.NL:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS, KEYS.HAYNES_PRO])
    case CountryCode.BE:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS, KEYS.EQUIPMENT_SPECIAL, KEYS.HAYNES_PRO])
    case CountryCode.FR:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS, KEYS.HAYNES_PRO])
    default:
      return new Set()
  }
}

function baseAddToOrderMenuByCountry(country: CountryCode): Set<string> {
  switch (country) {
    case CountryCode.NL:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS, KEYS.HAYNES_PRO])
    case CountryCode.BE:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS, KEYS.EQUIPMENT_SPECIAL, KEYS.HAYNES_PRO])
    case CountryCode.FR:
      return new Set([KEYS.TOOL_SPECIAL, KEYS.UNIVERSAL_PARTS, KEYS.HAYNES_PRO])
    default:
      return new Set()
  }
}

function addBandenNu(set: Set<string>, country?: string, orderSystem?: string): Set<string> {
  const isBelgium = country === CountryCode.BE
  const isLkqBelgium = (orderSystem ?? '').toLowerCase() === LKQ_ORDER_SYSTEM.toLowerCase()

  if (isBelgium && isLkqBelgium) {
    const s = new Set(set)
    s.add(KEYS.TYRE_BANDEN_NU)
    return s
  }
  return set
}

export function useFilteredExternalResources(
  externalResources: DashboardCatalogInfo | undefined,
  wholesalerBaseInfo: FormValues | undefined
): Result {
  return useMemo(() => {
    const country = wholesalerBaseInfo?.Country ?? CountryCode.NL
    const orderSystem = wholesalerBaseInfo?.OrderSystem

    // Build allowed sets per section
    let dashDashboardCatalogAllowed = baseDashboardCatalogByCountry(country as CountryCode)
    let orderInPreparationAllowed = baseOrderInPreparationByCountry(country as CountryCode)
    let addToOrderMenuAllowed = baseAddToOrderMenuByCountry(country as CountryCode)

    // Apply LKQ-Belgium rule
    dashDashboardCatalogAllowed = addBandenNu(dashDashboardCatalogAllowed, country, orderSystem)
    orderInPreparationAllowed = addBandenNu(orderInPreparationAllowed, country, orderSystem)
    addToOrderMenuAllowed = addBandenNu(addToOrderMenuAllowed, country, orderSystem)

    const filterByKeys = (arr: ExternalPortal[] | undefined, allowed: Set<string>) =>
      (arr ?? []).filter(item => allowed.has(item.UniqueKey)).map(item => ({ ...item, Selected: true }))

    const filteredExternalPortals = filterByKeys(externalResources?.ExternalPortal, dashDashboardCatalogAllowed)

    const filteredOrderInPreparation = filterByKeys(externalResources?.OrderInPreparation, orderInPreparationAllowed)

    const filteredAddToOrderMenu = filterByKeys(externalResources?.AddToOrderMenu, addToOrderMenuAllowed)

    return {
      filteredExternalPortals,
      filteredOrderInPreparation,
      filteredAddToOrderMenu
    }
  }, [externalResources, wholesalerBaseInfo?.Country, wholesalerBaseInfo?.OrderSystem])
}
